import React from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Send,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import PMRLogo from "./PMRLogo";
import { CONTACT, NAV_LINKS, DESTINATIONS } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const Footer = () => {
  const { toast } = useToast();
  const subscribe = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    if (!email) return;
    toast({ title: "Welcome aboard!", description: `${email} subscribed to travel deals.` });
    e.target.reset();
  };

  return (
    <footer className="bg-slate-950 text-slate-300">
      {/* Newsletter */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Get exclusive travel deals
            </h3>
            <p className="mt-2 text-slate-400">
              Subscribe & save up to 30% on early-bird packages every month.
            </p>
          </div>
          <form onSubmit={subscribe} className="flex gap-2 max-w-md md:ml-auto w-full">
            <Input
              name="email"
              type="email"
              required
              placeholder="your@email.com"
              className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12"
            />
            <Button type="submit" className="h-12 px-6 bg-orange-500 hover:bg-orange-600">
              <Send className="w-4 h-4 mr-2" /> Subscribe
            </Button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-lg">
              <PMRLogo size={30} color="#0f766e" accent="#f97316" />
            </div>
            <div>
              <div className="font-bold text-white text-lg" style={{ fontFamily: "'Poppins', sans-serif" }}>PMR Travels</div>
              <div className="text-[10px] uppercase tracking-widest text-orange-400">Travel with PMR Safe & Secure</div>
            </div>
          </Link>
          <p className="mt-4 text-sm leading-relaxed text-slate-400">
            Your trusted travel partner since 2025. Handcrafted holidays, luxury transport & 24/7 human care.
          </p>
          <div className="mt-5 flex gap-3">
            {[Facebook, Instagram, Twitter, Youtube].map((Icon, i) => (
              <a
                key={i}
                href="#"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-orange-500 hover:text-white flex items-center justify-center transition"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-orange-400 transition">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/blog" className="hover:text-orange-400 transition">
                Blog
              </Link>
            </li>
            <li>
              <Link to="/faq" className="hover:text-orange-400 transition">
                FAQ
              </Link>
            </li>
            <li>
              <Link to="/cancel" className="hover:text-orange-400 transition">
                Cancel Ticket
              </Link>
            </li>
            <li>
              <Link to="/refund-policy" className="hover:text-orange-400 transition">
                Refund Policy
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Top Destinations</h4>
          <ul className="space-y-2 text-sm">
            {DESTINATIONS.slice(0, 6).map((d) => (
              <li key={d.id}>
                <Link to="/destinations" className="hover:text-orange-400 transition">
                  {d.name}, {d.state}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4">Contact Us</h4>
          <div className="mb-3">
            <div className="font-bold text-white text-base">PMR Travels</div>
          </div>
          <ul className="space-y-3 text-sm">
            <li className="flex gap-3">
              <MapPin className="w-4 h-4 shrink-0 text-orange-400 mt-0.5" />
              <span className="text-slate-400">{CONTACT.address}</span>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="w-4 h-4 shrink-0 text-orange-400" />
              <a href={`tel:${CONTACT.phone}`} className="hover:text-orange-400">{CONTACT.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail className="w-4 h-4 shrink-0 text-orange-400" />
              <a href={`mailto:${CONTACT.email}`} className="hover:text-orange-400">{CONTACT.email}</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} PMR Travels. All rights reserved.</div>
          <div className="flex gap-5">
            <a href="#" className="hover:text-orange-400">Privacy Policy</a>
            <a href="#" className="hover:text-orange-400">Terms of Service</a>
            <Link to="/refund-policy" className="hover:text-orange-400">Refund Policy</Link>
            <Link to="/cancel" className="hover:text-orange-400">Cancel Ticket</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
